import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { useTheme } from '../../contexts/ThemeContext'

export const CustomsPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const customsInfo = [
    {
      title: 'EORI Number',
      description: 'Economic Operators Registration and Identification',
      details: [
        'Required for all import/export activities with the EU',
        'UK EORI for GB trade',
        'EU EORI for direct EU warehouse operations',
        'Free to obtain, takes 2-5 business days',
      ],
    },
    {
      title: 'Customs Declarations',
      description: 'Documentation requirements for cross-border trade',
      details: [
        'Commercial invoice with full product details',
        'Packing list for shipments',
        'HS/Commodity codes for each product',
        'Certificate of Origin where applicable',
      ],
    },
    {
      title: 'Import Duties',
      description: 'Tariffs and duties on imports into the EU',
      details: [
        'Duty rates vary by HS code (0-17% typical)',
        'Duty calculated on CIF value',
        'Import VAT charged on top of duties',
        'Preferential rates may apply with certificates',
      ],
    },
    {
      title: 'Rules of Origin',
      description: 'Determining product origin for preferential treatment',
      details: [
        'UK-EU TCA allows zero-tariff trade for qualifying goods',
        'Products must meet rules of origin criteria',
        'Self-certification or EUR.1 certificates',
        'Record keeping for 4+ years required',
      ],
    },
  ]

  const commonDutyRates = [
    { category: 'Clothing & Textiles', rate: '12%' },
    { category: 'Electronics', rate: '0-3%' },
    { category: 'Footwear', rate: '8-17%' },
    { category: 'Toys', rate: '0-4.7%' },
    { category: 'Cosmetics', rate: '0%' },
    { category: 'Furniture', rate: '0-5.6%' },
    { category: 'Food Products', rate: 'Variable' },
    { category: 'Automotive Parts', rate: '3-4.5%' },
  ]

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              cursor: 'pointer',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            &larr; Back to Compliance Center
          </button>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Customs & Import Compliance
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Import duties, customs procedures, and documentation requirements
          </p>
        </div>

        {/* Post-Brexit Alert */}
        <Card style={{ marginBottom: '32px', borderLeft: '4px solid #FF6B35' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '32px' }}>&#128666;</span>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                Post-Brexit UK-EU Trade
              </h3>
              <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                Since January 2021, customs declarations are required for all UK-EU trade. The UK-EU Trade
                and Cooperation Agreement provides zero tariffs for qualifying goods, but customs formalities
                still apply. Consider an EU fulfillment hub to simplify customer experience.
              </p>
            </div>
          </div>
        </Card>

        {/* Customs Information Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '32px' }}>
          {customsInfo.map((info) => (
            <Card key={info.title}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                {info.title}
              </h3>
              <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '16px' }}>
                {info.description}
              </p>
              <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '14px', lineHeight: 1.8 }}>
                {info.details.map((detail, idx) => (
                  <li key={idx}>{detail}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Common Duty Rates */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>
            Common EU Import Duty Rates
          </h2>
          <p style={{ color: colors.textSecondary, marginBottom: '20px', fontSize: '14px' }}>
            Note: These are indicative rates. Actual duty depends on specific HS code classification.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {commonDutyRates.map((item) => (
              <div
                key={item.category}
                style={{
                  padding: '16px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '24px', fontWeight: 700, color: colors.primary, marginBottom: '4px' }}>
                  {item.rate}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>{item.category}</div>
              </div>
            ))}
          </div>
        </Card>
      </main>
    </div>
  )
}
